import { Button, Space } from 'antd';
import { getDoc } from 'firebase/firestore';
import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { NotificationContext } from '@client/context/NotificationContext';
import { useError } from '@client/hooks/useError';
import { useSchedulerQueries } from '@client/queries/useSchedulerQueries';
import { useSubscriptionQueries } from '@client/queries/useSubscriptionQueries';
import { getTenantsBySubscription } from '@client/services/tenant.service';
import { useAssigned } from '@client/store/assigned.store';
import { useLoading } from '@client/store/loading.store';
import { ERoutePaths } from '@client/types/router.type';
import {
  EErrorType,
  CSchedulerEntityPaths,
  EStatus,
  type IFeature,
  type ISubscription,
  type TErrorData,
} from '@common/types';

export const useSubscription = () => {
  const {
    useUserSubscription,
    useDeleteSubscription,
    useUpdateSubscription,
    useCreateSubscription,
    useToggleActive,
  } = useSubscriptionQueries();
  const { useDeleteEntitySchedulers } = useSchedulerQueries();

  const { setError } = useError();
  const { notificationApi } = useContext(NotificationContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const { assigned: features } = useAssigned<IFeature>();

  // Query for user subscription
  const {
    data: subscription,
    refetch,
    isLoading,
    isFetching,
    isError,
    error,
  } = useUserSubscription(params.subscriptionId);
  const { setLoading } = useLoading(false);

  // Mutation for deactivating a subscription
  const deleteMutation = useDeleteSubscription();
  const deleteAllSchedulers = useDeleteEntitySchedulers<ISubscription>(
    CSchedulerEntityPaths.SUBSCRIPTIONS,
  );
  const createMutation = useCreateSubscription();
  const updateMutation = useUpdateSubscription();
  const activeMutation = useToggleActive();

  useEffect(() => {
    if (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.DOCUMENT_NOT_FOUND,
      });

      navigate(ERoutePaths.SUBSCRIPTIONS);
    }
  }, [error, navigate, setError]);

  const onGetFeatures = async () => {
    const features = [];
    try {
      setLoading(true);
      if (!subscription) return features;
      for (const feature of subscription?.assignedFeatures || []) {
        const docSnap = await getDoc(feature);
        if (docSnap.exists()) {
          features.push({ id: feature.id, ...docSnap.data() } as IFeature);
        } else {
          throw new Error(`${EErrorType.FAILED_GET}: ${feature.id}`);
        }
      }
      return features;
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_GET_ALL,
      });
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (entity: ISubscription): Promise<void> => {
    try {
      setLoading(true);
      await Promise.all([
        deleteMutation.mutateAsync(entity.id as string),
        deleteAllSchedulers.mutateAsync(entity.id as string),
      ]);

      navigate(ERoutePaths.SUBSCRIPTIONS);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_DELETE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async (
    id: ISubscription['id'],
    data: ISubscription,
  ): Promise<void> => {
    try {
      setLoading(true);
      await updateMutation.mutateAsync({ id, data, features });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_UPDATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onCreate = async (data: ISubscription): Promise<ISubscription> => {
    try {
      setLoading(true);
      return createMutation.mutateAsync({ data, features });
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_CREATE,
      });
    } finally {
      setLoading(false);
    }
  };

  const onActivate = async (data: ISubscription): Promise<void> => {
    try {
      setLoading(true);

      if (data.status === EStatus.ACTIVE) {
        const tenants = await getTenantsBySubscription(data.id as string);

        if (tenants.length > 0) {
          const tenant = tenants[0];
          const key = `deactivate-blocked-${data.id}`;

          notificationApi?.warning({
            key,
            title: t('subscription.msg.deactivateBlocked'),
            description: t('subscription.msg.deactivateBlockedDescription', {
              tenantName: tenant.name,
            }),
            actions: (
              <Space>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    notificationApi.destroy(key);
                    navigate(
                      ERoutePaths.TENANT_EDIT.replace(
                        ':tenantId',
                        tenant.id as string,
                      ),
                    );
                  }}
                >
                  {t('subscription.actions.navigateToTenant')}
                </Button>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => notificationApi.destroy(key)}
                >
                  {t('actions.close')}
                </Button>
              </Space>
            ),
          });

          return;
        }
      }

      await activeMutation.mutateAsync(data);
    } catch (error) {
      setError({
        data: error as TErrorData,
        description: EErrorType.FAILED_ACTIVATE,
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading: isLoading || isFetching,
    isError,
    error,
    subscription,
    onDelete,
    onUpdate,
    onCreate,
    onActivate,
    onGetFeatures,
    refetch,
  };
};
